import React, { useState, useEffect } from 'react';
import {
  Button,
  Grid,
  Typography,
  Box,
  Skeleton,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import './SectionList.css';
import { ISection } from '../../../../api/load-balancer';

interface SectionListProps {
  sections: ISection[];
  isLoading: boolean;
  error: any;
  language: string;
  onEdit: (sectionId: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onTogglePublish: (index: number) => void;
  onDelete: (index: number) => void;
}

const SectionList: React.FC<SectionListProps> = ({
  sections,
  onEdit,
  isLoading,
  error,
  language,
  onMoveUp,
  onMoveDown,
  onTogglePublish,
  onDelete,
}) => {
  const [sectionList, setSectionList] = useState<ISection[]>(sections);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({ index: -1, action: '', message: '' });

  useEffect(() => {
    setSectionList(sections);
  }, [sections]);

  const handleOpenDialog = (index: number, action: string, message: string) => {
    setDialogInfo({ index, action, message });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogInfo({ index: -1, action: '', message: '' });
  };

  const handleConfirmAction = async () => {
    const { index, action } = dialogInfo;
    handleCloseDialog();

    if (action === 'togglePublish') {
      await onTogglePublish(index);
    } else if (action === 'delete') {
      await onDelete(index);
    }
  };

  if (isLoading) {
    return (
      <Grid container spacing={2} mt="10px">
        {[...Array(5)].map((_, index) => (
          <Grid item xs={12} key={index}>
            <Skeleton variant="rectangular" width="100%" height={50} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return <Typography color="error">Failed to load sections: {error.message}</Typography>;
  }

  return (
    <>
      <Grid container spacing={2} mt="10px">
        {sectionList.map((section, index) => (
          <Grid item xs={12} key={section.id}>
            <Box display="flex" alignItems="center" justifyContent="space-around" gap="10px">
              <Box display="flex" alignItems="center" flex="0 0 40%">
                <Box
                  component="img"
                  src={`quickstart/${language}/images/${section.icon}`}
                  sx={{ height: 40, width: 40, mr: 2 }}
                />
                <Typography className="section-wrap" variant="h6">
                  {section.sectionName}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap="10px">
                <Box className="section-position-status" display="flex" alignItems="center" gap="10px">
                  <Box className="section-position" display="flex" alignItems="center" gap="10px">
                    <Box display="flex" alignItems="center">
                      <Button style={{ minWidth: '40px' }} onClick={() => onMoveUp(index)} disabled={index === 0}>
                        <ArrowCircleUpIcon style={{ color: index === 0 ? 'gray' : 'red' }} />
                      </Button>
                      <Button
                        style={{ minWidth: '40px' }}
                        onClick={() => onMoveDown(index)}
                        disabled={index === sectionList.length - 1}
                      >
                        <ArrowCircleDownIcon style={{ color: index === sectionList.length - 1 ? 'gray' : 'blue' }} />
                      </Button>
                    </Box>
                    <Typography minWidth="88px" variant="h6">
                      Position: {index + 1}
                    </Typography>
                  </Box>
                  <Box width="80px">
                    <Typography variant="body1" color={section.isPublish === true ? 'green' : 'red'} sx={{ mr: 2 }}>
                      {section.isPublish === true ? 'ONLINE' : 'OFFLINE'}
                    </Typography>
                  </Box>
                </Box>
                <Box className="button-action-section" display="flex" flexWrap="wrap" alignItems="flex-end">
                  <Switch
                    checked={section.isPublish}
                    onChange={() =>
                      handleOpenDialog(
                        index,
                        'togglePublish',
                        `Are you sure you want to ${section.isPublish ? 'offline' : 'publish'} this section?`
                      )
                    }
                    color="success"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' } }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => onEdit(section.id)}
                    sx={{ mr: 1, mt: 1, width: '88px', backgroundColor: '#53b9d8 !important' }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenDialog(index, 'delete', 'Are you sure you want to delete this section?')}
                    sx={{ mt: 1, width: '88px', backgroundColor: '#ed6923 !important' }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Dialog open={openDialog}>
        <DialogTitle fontSize="20px" fontWeight="600">
          Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText fontSize="16px" fontWeight="500">
            {dialogInfo.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SectionList;
